import * as React from "react"
import { CmsAuthorizationModal } from "@modules/cms/authorization/components/CmsAuthorizationModal"
import { CmsVendor, CmsProvider, Repository } from "@modules/graphql/types"
import { OnboardingSiteType } from "@modules/onboarding/shared/types"
import {
  useCmsIntegrationForProvisioningQuery,
  useFinalizeSiteProvisioningMutation,
  useProvisionedSiteDetailsQuery,
} from "@modules/site/create/Provision/queries.generated"
import {
  ErrorAlert,
  useTriggerErrorAlert,
} from "@modules/ui/components/ErrorAlert"
import Loading from "@modules/ui/components/Loading"
import { NarrowSingleColumn } from "@modules/ui/layouts/Containers"
import { Button, Card, CardHeader, CardSection, Text } from "gatsby-interface"
import { MdArrowForward, MdCheck } from "react-icons/md"
import { cmsVendorIcons, siteTypeIcons } from "@modules/site/create/icons"

export type ProvisionCmsIntegrationProps = {
  siteId: string | null
  starterVendor: CmsVendor
  starterCmsTemplateId: string
  starterCmsProviderFormattedName: string
  onNext: () => void
}

export function ProvisionCmsIntegration({
  siteId,
  starterVendor,
  starterCmsTemplateId,
  starterCmsProviderFormattedName,
  onNext,
}: ProvisionCmsIntegrationProps) {
  const {
    data: cmsIntegrationsData,
    error: integrationError,
    loading: integrationLoading,
    refetch,
  } = useCmsIntegrationForProvisioningQuery({
    variables: {
      siteId,
      vendor: starterVendor,
    },
    fetchPolicy: `cache-and-network`,
  })

  const {
    data: provisionedSiteDetails,
    loading: provisionedSiteLoading,
    error: provisionedSiteError,
  } = useProvisionedSiteDetailsQuery({
    variables: { siteId },
    fetchPolicy: `cache-and-network`,
  })

  const [isLoading, setLoading] = React.useState(false)
  const [isConnected, setConnected] = React.useState(false)
  const [isOpenedModal, toggleModal] = React.useState(false)
  const [finalizeProvisioning] = useFinalizeSiteProvisioningMutation()
  const [setError, errorAlert] = useTriggerErrorAlert()

  React.useEffect(() => {
    const onConnected = async () => {
      setLoading(true)
      return finalizeProvisioning({
        variables: {
          siteId,
          cmsTemplateId: starterCmsTemplateId,
        },
      })
        .then(({ data }) => {
          setLoading(false)
          if (!data || !data.finalizeSiteProvisioning) {
            return setError({
              message: "Unknown error occurred. Please try again.",
            })
          }
          if (!data.finalizeSiteProvisioning.success) {
            return setError({
              message:
                data.finalizeSiteProvisioning.message ||
                "Unknown error occurred. Please try again.", // More smells
            })
          }
          onNext()
        })
        .catch(e => {
          setLoading(false)
          setError(e)
        })
    }
    if (isConnected) {
      onConnected()
    }
  }, [isConnected])

  if (integrationLoading || provisionedSiteLoading) {
    return (
      <NarrowSingleColumn>
        <Loading
          delay={1000}
          message="Loading your repository..."
          bufferSize="padded"
        />
      </NarrowSingleColumn>
    )
  }

  if (integrationError || provisionedSiteError) {
    return (
      <NarrowSingleColumn>
        {[integrationError, provisionedSiteError].map((error, idx) => {
          return error ? (
            <ErrorAlert key={idx}>{error.message}</ErrorAlert>
          ) : null
        })}
      </NarrowSingleColumn>
    )
  }

  const cmsIntegrationForProvisioning =
    cmsIntegrationsData?.cmsIntegrationForProvisioning
  const {
    name: cmsName,
    formattedName: formattedCmsName,
    connected,
  } = cmsIntegrationForProvisioning as CmsProvider // This is a big smell

  if (!isConnected && !!connected) {
    setConnected(!!connected)
  }

  const repositoryFromSite = provisionedSiteDetails?.repositoryFromSite
  const { nameWithOwner } = repositoryFromSite as Repository // Also smelly over here

  const Icon = starterVendor
    ? cmsVendorIcons[starterVendor]
    : starterCmsTemplateId
    ? siteTypeIcons[starterCmsTemplateId]
    : null

  return isLoading ? (
    <Loading
      delay={1000}
      message="Starting your project..."
      bufferSize="padded"
    />
  ) : (
    <React.Fragment>
      <Card>
        <CardHeader
          // not ideal, but how we need to override font family to be Inter
          css={theme => ({
            h3: {
              fontFamily: theme.fonts.heading,
            },
          })}
          title="Connect Integration"
        />
        <CardSection>
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div css={{ display: "flex", alignItems: "center" }}>
              {Icon ? Icon : null}
              <Text
                noMarginBottom
                css={theme => ({ marginLeft: theme.space[2] })}
              >
                {starterCmsProviderFormattedName}
              </Text>
            </div>
            {connected ? (
              <Button
                tone="SUCCESS"
                rightIcon={<MdCheck />}
                onClick={() => toggleModal(true)}
              >
                Connected
              </Button>
            ) : (
              <Button
                variant="PRIMARY"
                rightIcon={<MdArrowForward />}
                onClick={() => toggleModal(true)}
              >
                Connect
              </Button>
            )}
          </div>
          <Text
            css={theme => ({
              marginTop: theme.space[5],
            })}
          >
            Connect the CMS to automatically install the Gatsby Preview
            extension and set up Gatsby Cloud project environment variables.
          </Text>
          {errorAlert}
        </CardSection>
      </Card>
      {isOpenedModal && siteId && formattedCmsName && (
        <CmsAuthorizationModal
          closeModal={() => toggleModal(false)}
          siteId={siteId}
          formattedName={formattedCmsName}
          isConnected={!!connected}
          orgSiteName={nameWithOwner}
          name={cmsName}
          siteType={OnboardingSiteType.Provision}
          refetch={refetch}
          isOpen={isOpenedModal}
        />
      )}
    </React.Fragment>
  )
}
